import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getStewardInbox,
  markStewardInboxRead,
  type StewardInboxItem,
} from '../../../lib/api'

interface Props {
  /** Open an inbox item — it's a session, so this rehydrates it in the chat. For a
   *  decision, the options surface as clickable chips above the input. */
  onOpenItem: (sessionId: string, options?: string[]) => void
}

/**
 * The agent→consumer inbox, in the header. A badge counts DECISIONS that need you
 * (notices don't add to it). Click → a popover: decisions first ("Needs you"), then
 * unread notices ("Heads up"). Opening a decision rehydrates the session so you answer
 * in chat (RetrieveNode runs the parked action). Opening a notice marks it read — it
 * auto-ages out. Polls every 30s; refreshes on open. This is the N2 defense made visible:
 * the badge is the single unresolved thing, never a feed of everything.
 */
export function InboxButton({ onOpenItem }: Props) {
  const [items, setItems] = useState<StewardInboxItem[]>([])
  const [badge, setBadge] = useState(0)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const refresh = useCallback(async () => {
    try {
      const inbox = await getStewardInbox()
      setItems(inbox.items)
      setBadge(inbox.badge)
    } catch {
      /* inbox is best-effort — never break the page on a fetch error */
    }
  }, [])

  useEffect(() => {
    const initial = window.setTimeout(() => void refresh(), 0)
    const poll = window.setInterval(() => void refresh(), 30_000)
    return () => {
      window.clearTimeout(initial)
      window.clearInterval(poll)
    }
  }, [refresh])

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  function handleItem(item: StewardInboxItem) {
    setOpen(false)
    if (item.kind === 'notice' && !item.read_at) {
      markStewardInboxRead(item.session_id)
        .then(refresh)
        .catch(() => {})
    }
    const options =
      item.kind === 'decision' ? item.decision_spec?.questions?.[0]?.options ?? [] : undefined
    onOpenItem(item.session_id, options)
  }

  const decisions = items.filter((i) => i.kind === 'decision')
  const notices = items.filter((i) => i.kind === 'notice')

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => {
          setOpen((o) => !o)
          refresh()
        }}
        className="relative flex items-center gap-1 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        title="Inbox"
      >
        Inbox
        {badge > 0 && (
          <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-medium text-background">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-80 overflow-hidden rounded-lg border border-border bg-background shadow-lg">
          {items.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Nothing needs you.
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto py-1">
              {decisions.length > 0 && (
                <div className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Needs you
                </div>
              )}
              {decisions.map((d) => (
                <button
                  key={d.session_id}
                  onClick={() => handleItem(d)}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-muted"
                >
                  <span className="text-sm text-foreground">{d.name}</span>
                  <span className="text-[11px] text-muted-foreground">a decision — tap to answer</span>
                </button>
              ))}
              {notices.length > 0 && (
                <div className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Heads up
                </div>
              )}
              {notices.map((n) => (
                <button
                  key={n.session_id}
                  onClick={() => handleItem(n)}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-muted"
                >
                  <span className="text-sm text-foreground">{n.name}</span>
                  <span className="text-[11px] text-muted-foreground">it did this for you</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
